import { useCallback, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useClient } from 'urql';

import { GetAutocompleteSuggestionsDocument } from '@/modules/knowledge-base-copilot/graphql/queries/generated/autocompleteSuggestions';
import { AutocompletePurposeEnum } from '@/types/schema';

function hasSpaceFollowedByCharacter(input: string) {
  const regex = /\s\w/; // Matches a space followed by any character
  return regex.test(input);
}

function useAutocompleteSuggestions() {
  const client = useClient();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // using useRef instead of state to maintain cache helps avoid rerenders
  const suggestionsCacheRef = useRef<Record<string, string[]>>({});

  const fetchSuggestions = useCallback(
    async (prefix: string) => {
      const { data, error } = await client
        .query(
          GetAutocompleteSuggestionsDocument,
          {
            params: {
              numberOfSuggestions: 4,
              purpose: AutocompletePurposeEnum.ChatWithKnowledgeBase,
              prefix,
            },
          },
          { requestPolicy: 'cache-and-network' },
        )
        .toPromise();

      const res = data?.getAutocompleteSuggestions?.suggestions || [];

      return { res, error };
    },
    [client],
  );

  const handleAutoCompleteSuggestions = useCallback(
    async (value: string) => {
      if (!value?.trim()?.length || !hasSpaceFollowedByCharacter(value)) {
        setSuggestions([]);
        return;
      }
      const lowercaseValue = value.toLowerCase();

      if (suggestionsCacheRef.current[lowercaseValue]) {
        setSuggestions(suggestionsCacheRef.current[lowercaseValue]);
        return;
      }

      const { res, error } = await fetchSuggestions(lowercaseValue);
      if (res.length) {
        setSuggestions(res.slice(0, 4));
        suggestionsCacheRef.current[lowercaseValue] = res;
      } else if (error) {
        // In case of error, clear the suggestions, show nothing
        setSuggestions([]);
      }
    },
    [fetchSuggestions],
  );

  const debouncedHandling = useMemo(
    () => debounce(handleAutoCompleteSuggestions, 400),
    [handleAutoCompleteSuggestions],
  );

  return {
    suggestions,
    setSuggestions,
    handleAutoCompleteSuggestions: debouncedHandling,
  };
}

export default useAutocompleteSuggestions;
